/**
 * WHETHER THIS BLOCK MAY RECALL AN EARLIER ANSWER, and how to say so if it may not.
 *
 * The same three states the variables affordance uses, for the same reasons, so a teacher meets
 * one idea rather than two:
 *
 *   absent    the capability is off. No control — an inert button for a feature that does not
 *             exist is a false invitation.
 *   disabled  on, and this block is above the rung reached. The control STAYS, inert, so the
 *             toolbar is the same shape in every block and the gap reads as something to unlock.
 *   allowed   insert away.
 *
 * ## It answers the DESTINATION half only
 *
 * Recall widens along two axes — which answers may be recalled, and which blocks may hold a
 * recall — and a control is offered before a source is chosen. So this decides the control, and
 * the source half is applied to the offerable LIST instead. A teacher at rung 1 standing in a
 * Numerical block therefore sees the button and is offered only numerical answers, rather than
 * seeing a button that refuses everything it opens.
 *
 * ## It never narrows what ALIASES exist
 *
 * The alias list feeds resolution as much as insertion — every chip's rendering, the manager,
 * the status beside each name. Emptying it to gate insertion would turn authored chips into
 * dead text, which is the same mistake the variables affordance is careful not to make.
 */
import { useAnswerRefScope } from './answer-ref-context';
import { useBlockIdentity } from './fired-states';

import { useCapabilities } from '@/hooks/use-capabilities';
import { CAPABILITIES } from '@/lib/capabilities';
import { recallAllowedInBlock } from '@/lib/recall-rungs';

export type RecallAffordance = 'absent' | 'disabled' | 'allowed';

export function useRecallAffordance(): RecallAffordance {
  const { level } = useCapabilities();
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();

  const rung = level('answerRecall');
  if (rung <= 0) return 'absent';

  const self = identity?.blockId
    ? scope?.tasks.find(tk => tk.id === identity.taskId)?.blocks
      ?.find((b: { id: string }) => b.id === identity.blockId)
    : undefined;

  /*
   * An unknown block is ALLOWED, the same way the variables affordance leans and the opposite
   * of how `recallAllowedBetween` leans. A control mounted outside the providers is a harness,
   * not a teacher at a gated block, and a wrong "disabled" takes a control from someone
   * entitled to it. The validity check fails closed because it exists to refuse; this fails
   * open because it exists to offer.
   */
  if (!self?.type) return 'allowed';
  return recallAllowedInBlock(rung, self.type) ? 'allowed' : 'disabled';
}

/**
 * MAY THIS BLOCK BE DRIVEN BY AN EARLIER ANSWER — the mechanism, not the chip.
 *
 * Driven-by is bound to the TOP rung of Answer Recall, and the reason is in the engine rather
 * than in a preference: `driverRefusal` turns down a Numerical with `range`, a Slider with
 * `measurement`, a Compass with `measurement` — a number is a continuum and does not partition
 * into versions. Every answer that CAN drive is a choice or a word, which is the top rung's own
 * content. So a lower rung would offer a control whose every candidate it had already withheld.
 *
 * It also avoids a recall that turns load-bearing later: a text answer can change the correct
 * answer, and a teacher meets that the first time they meet text recall rather than afterwards.
 *
 * `inUse` is the trapdoor rule, the same one the scenario half states one file over: a block
 * already driven keeps the control however the capability stands, because this is the only way
 * to see what it follows or to unlink it. Gate the `+`, never the exit.
 *
 * Against the registry's rung COUNT rather than a literal 3, so inserting a rung moves this
 * with it instead of handing the mechanism to a new middle rung.
 */
export function useDrivenAllowed(inUse = false): boolean {
  const { level } = useCapabilities();
  const top = CAPABILITIES.find(c => c.id === 'answerRecall')?.rungs.length ?? 0;
  return inUse || (top > 0 && level('answerRecall') >= top);
}
